import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Activity } from "lucide-react";
import {
  useAllMonitoringPlanChanges,
  useMonitoringPlanAsOf,
} from "@/hooks/useMonitoringPlan";
import { cn } from "@/lib/utils";
import { formatRole } from "./planFormat";
import {
  adviceChanged,
  computeNetChanges,
  frequencyChanged,
  type NetChangeKind,
} from "./planDiff";
import type {
  MonitoringPlanChange,
  MonitoringPlanEntry,
} from "@/types/monitoring";

interface MonitoringAdviceHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
}

interface VisitGroup {
  kind: "visit";
  key: string;
  appointmentRef: string;
  /** When the advice was given: the visit's date, else its first record. */
  givenAt: string;
  latestAt: string;
  records: MonitoringPlanChange[];
}

interface BetweenItem {
  kind: "between";
  key: string;
  latestAt: string;
  record: MonitoringPlanChange;
}

type HistoryItem = VisitGroup | BetweenItem;

// A visit's records become one card: the plan given at that visit. A change
// made outside any visit stands alone, with who made it. Newest first.
const buildHistory = (changes: MonitoringPlanChange[]): HistoryItem[] => {
  const visits = new Map<string, VisitGroup>();
  const between: BetweenItem[] = [];
  for (const c of changes) {
    if (!c.appointment) {
      between.push({
        kind: "between",
        key: c.id,
        latestAt: c.created_at,
        record: c,
      });
      continue;
    }
    const g = visits.get(c.appointment.id) ?? {
      kind: "visit" as const,
      key: c.appointment.id,
      appointmentRef: c.appointment.zarity_appointment_id,
      givenAt: c.appointment.start_time || c.created_at,
      latestAt: c.created_at,
      records: [],
    };
    g.records.push(c);
    if (c.created_at > g.latestAt) g.latestAt = c.created_at;
    if (!c.appointment.start_time && c.created_at < g.givenAt)
      g.givenAt = c.created_at;
    visits.set(c.appointment.id, g);
  }
  // A visit that ended where it started is noise; the records stay for audit.
  const meaningful = [...visits.values()].filter(
    (g) => computeNetChanges(g.records).length > 0
  );
  return [...meaningful, ...between].sort((a, b) =>
    b.latestAt.localeCompare(a.latestAt)
  );
};

const formatWho = (actor: MonitoringPlanChange["changed_by"]) =>
  actor
    ? `${actor.name}${actor.role ? ` · ${formatRole(actor.role)}` : ""}`
    : "Unknown";

/** Whoever made most of a visit's changes; ties go to the latest. */
const topActor = (records: MonitoringPlanChange[]) => {
  const counts = new Map<string, { n: number; label: string }>();
  for (const r of records) {
    const label = formatWho(r.changed_by);
    const c = counts.get(label) ?? { n: 0, label };
    c.n += 1;
    counts.set(label, c);
  }
  return [...counts.values()].sort((a, b) => b.n - a.n)[0]?.label ?? "Unknown";
};

// Light-mode --success is near-black forest; "New" borrows the palette's
// mid-forest chart hue so it reads green (same as MonitoringAdviceCard).
const NEW_GREEN_BADGE =
  "bg-[oklch(var(--chart-3)/0.15)] text-[oklch(var(--chart-3))] dark:bg-success/20 dark:text-success";

const KIND_BADGE: Record<
  NetChangeKind,
  {
    variant: "success" | "warning" | "critical";
    label: string;
    className?: string;
  }
> = {
  new: { variant: "success", label: "New", className: NEW_GREEN_BADGE },
  changed: { variant: "warning", label: "Changed" },
  stopped: { variant: "critical", label: "Stopped" },
};

const ACTION_KIND: Record<MonitoringPlanChange["action"], NetChangeKind> = {
  added: "new",
  changed: "changed",
  stopped: "stopped",
};

interface PlanRowData {
  trackerId: number;
  trackerName: string;
  kind: NetChangeKind | null;
  entry: MonitoringPlanEntry | null;
  before: MonitoringPlanEntry | null;
}

const PLAN_GRID =
  "grid grid-cols-[minmax(0,4fr)_minmax(0,3fr)_minmax(0,4fr)] gap-3";

// The card header: when, which visit, and who, as pills.
const CardPills = ({
  date,
  withTime = false,
  visit,
  who,
}: {
  date: string;
  /** Between-visit changes carry their time; a visit is known by its day. */
  withTime?: boolean;
  visit: string;
  who: string;
}) => (
  <div className="flex flex-wrap items-center gap-1.5 pb-2">
    <Badge variant="outline">
      {format(parseISO(date), withTime ? "d MMM yyyy, HH:mm" : "d MMM yyyy")}
    </Badge>
    <Badge variant="outline">{visit}</Badge>
    <Badge variant="secondary">{who}</Badge>
  </div>
);

const PlanHeader = () => (
  <div
    className={cn(
      PLAN_GRID,
      "z-eyebrow text-muted-foreground pb-1.5 border-b border-border/60"
    )}
  >
    <span>Tracker</span>
    <span>Frequency</span>
    <span>Advice</span>
  </div>
);

// One row per tracker. A change reads "old → new" in place; stopped rows are
// struck through.
const PlanLine = ({ row }: { row: PlanRowData }) => {
  const stopped = row.kind === "stopped";
  const shown = stopped ? row.before : row.entry;
  const freqWas =
    row.kind === "changed" &&
    row.before &&
    row.entry &&
    frequencyChanged(row.before, row.entry);
  const adviceWas =
    row.kind === "changed" &&
    row.before &&
    row.entry &&
    adviceChanged(row.before, row.entry);
  const badge = row.kind ? KIND_BADGE[row.kind] : null;

  return (
    <div
      className={cn(
        PLAN_GRID,
        "items-center py-2 border-b border-border/60 last:border-0"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
        <span
          className={cn(
            "z-secondary break-words min-w-0",
            stopped
              ? "text-muted-foreground line-through"
              : "text-foreground font-medium"
          )}
        >
          {row.trackerName}
        </span>
        {badge && (
          <Badge variant={badge.variant} className={badge.className}>
            {badge.label}
          </Badge>
        )}
      </div>
      <span
        className={cn(
          "z-secondary",
          stopped ? "text-muted-foreground line-through" : "text-foreground"
        )}
      >
        {freqWas && (
          <span className="text-muted-foreground line-through mr-1.5">
            {row.before?.frequency?.name ?? "—"}
          </span>
        )}
        {shown?.frequency?.name ?? "—"}
      </span>
      <span
        className={cn(
          "z-secondary text-muted-foreground",
          stopped && "line-through"
        )}
      >
        {adviceWas && (
          <span className="line-through mr-1.5">
            {row.before?.advice || "No advice"}
          </span>
        )}
        {shown?.advice || "—"}
      </span>
    </div>
  );
};

// The plan given at a visit: what was active when the visit ended. Changes
// made at this visit come first, unchanged trackers after, stopped ones last.
const VisitCard = ({
  group,
  patientId,
}: {
  group: VisitGroup;
  patientId: string;
}) => {
  const nets = useMemo(() => computeNetChanges(group.records), [group.records]);
  const { data: planAtEnd, isLoading } = useMonitoringPlanAsOf(
    patientId,
    group.latestAt,
    true
  );

  const rows = useMemo<PlanRowData[]>(() => {
    const netByTracker = new Map(nets.map((n) => [n.trackerId, n]));
    const active: PlanRowData[] = (planAtEnd ?? []).map((e) => {
      const net = netByTracker.get(e.tracker.id);
      return {
        trackerId: e.tracker.id,
        trackerName: e.tracker.name,
        kind: net && net.kind !== "stopped" ? net.kind : null,
        entry: e,
        before: net?.before ?? null,
      };
    });
    const stopped: PlanRowData[] = nets
      .filter((n) => n.kind === "stopped")
      .map((n) => ({
        trackerId: n.trackerId,
        trackerName: n.trackerName,
        kind: "stopped",
        entry: null,
        before: n.before,
      }));
    const order = (r: PlanRowData) => (r.kind === null ? 1 : 0);
    return [...active.sort((a, b) => order(a) - order(b)), ...stopped];
  }, [planAtEnd, nets]);

  const notes = useMemo(
    () => [
      ...new Set(
        group.records
          .map((r) => r.note?.trim())
          .filter((n): n is string => !!n && n.toLowerCase() !== "backfill")
      ),
    ],
    [group.records]
  );

  // Same quiet fill as the card's "Ongoing plan": this is a plan given at a visit.
  return (
    <div className="bg-muted/40 border border-border/60 rounded-xl px-4 py-3">
      <CardPills
        date={group.givenAt}
        visit={group.appointmentRef}
        who={topActor(group.records)}
      />
      <PlanHeader />
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        rows.map((r) => <PlanLine key={r.trackerId} row={r} />)
      )}
      {notes.map((n) => (
        <p key={n} className="z-hint text-muted-foreground pt-1">
          {n}
        </p>
      ))}
    </div>
  );
};

// A single change made outside any visit: dated when it was changed, with
// who made it.
const BetweenCard = ({ item }: { item: BetweenItem }) => {
  const r = item.record;
  const kind = ACTION_KIND[r.action];
  const note = r.note?.trim();
  const row: PlanRowData = {
    trackerId: r.tracker.id,
    trackerName: r.tracker.name,
    kind,
    entry: r.after,
    before: r.before,
  };
  // Citron tint: a one-off change outside any visit, so it stands apart.
  return (
    <div className="bg-accent/15 border border-accent/40 rounded-xl px-4 py-3">
      <CardPills
        date={r.created_at}
        withTime
        visit="Between visits"
        who={formatWho(r.changed_by)}
      />
      <PlanHeader />
      <PlanLine row={row} />
      {note && note.toLowerCase() !== "backfill" && (
        <p className="z-hint text-muted-foreground pt-1">{note}</p>
      )}
    </div>
  );
};

export const MonitoringAdviceHistoryDialog = ({
  open,
  onOpenChange,
  patientId,
}: MonitoringAdviceHistoryDialogProps) => {
  const { changes, isLoading, isComplete } = useAllMonitoringPlanChanges(
    patientId,
    open
  );
  const groups = useMemo(() => buildHistory(changes), [changes]);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0 border-border/60">
        <DialogHeader className="px-6 py-4 border-b border-border/60 flex flex-row items-center justify-between">
          <DialogTitle className="z-h2 text-foreground">
            Monitoring Advice - History
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 max-h-[600px]">
          {isLoading || (!isComplete && changes.length === 0) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-muted rounded-full">
                  <Activity className="h-8 w-8 text-muted-foreground" />
                </div>
              </div>
              <p className="z-h3 text-foreground">No History Available</p>
              <p className="mt-1">
                Changes to this patient&apos;s monitoring plan will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((g) =>
                g.kind === "visit" ? (
                  <VisitCard key={g.key} group={g} patientId={patientId} />
                ) : (
                  <BetweenCard key={g.key} item={g} />
                )
              )}
              {!isComplete && (
                <div className="flex justify-center p-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border/60 bg-card">
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
